<script>
  import { onMount } from 'svelte';
  import Navbar from '../components/Navbar.svelte';
  import AnnouncementBar from '../components/AnnouncementBar.svelte';
  import Hero from '../components/Hero.svelte';
  import ProductGrid from '../components/ProductGrid.svelte';
  import LookbookSection from '../components/LookbookSection.svelte';
  import ArticleSection from '../components/ArticleSection.svelte';
  import CategoryPromo from '../components/CategoryPromo.svelte';
  import { pickPromoted } from '../lib/homepage.js';
  import Footer from '../components/Footer.svelte';
  import Loader from '../components/Loader.svelte';
  import { loadStoreData, liveData } from '../lib/storeData.js';
  import { watchLaunches } from '../lib/clock.js';
  // Checks for changes now and then while the page is open (and on coming back to the tab), so a drop launching shows its hero without a reload.
  onMount(() => watchLaunches());

  let data = null;
  let loading = true;

  onMount(async () => {
    try { data = await loadStoreData(); }
    catch (e) { console.error(e); }
    finally { loading = false; }
  });

  // A product going on sale on this page (its countdown ended) reloads the store data: take it, so a drop's hero appears at launch too.
  $: if ($liveData && data && $liveData !== data) data = $liveData;
  // The hero can be switched off in Settings → Homepage; the page then leads with New Arrivals. A live drop with its own hero replaces it.
  $: hero = data?.dropHero || data?.site?.hero;
  $: heroEnabled = Boolean(data?.dropHero) || data?.site?.hero?.enabled !== false;

  // Settings → Homepage can switch the whole featured lookbook / article section off.
  $: editorialEnabled = data?.site?.featuredEditorialEnabled !== false;

  $: featuredId = data?.site?.featuredLookbook;
  $: editorialType = data?.site?.featuredEditorialType || 'lookbook';
  
  // Settings → Homepage can promote one category in its own section (null when off, or the category has no products).
  $: promo = data ? pickPromoted(data.products, data.categories, data.site.promotedCategory) : null;
  $: promoAfterEditorial = data?.site?.promotedCategory?.position === 'after-editorial';

  $: allLookbooks = data?.lookbooks ?? [];
  $: allArticles = data?.community ?? [];
  
  $: featuredLookbook = editorialType === 'lookbook' 
    ? (featuredId ? allLookbooks.find(lb => lb.id === featuredId) || allLookbooks[0] || null : allLookbooks[0] || null)
    : null;
    
  $: featuredArticle = editorialType === 'article'
    ? (featuredId ? allArticles.find(a => a.id === featuredId) || allArticles.find(a => a.published) || null : allArticles.find(a => a.published) || null)
    : null;
</script>

<svelte:head>
  <title>{data ? (data.site.metaTitle || data.site.name) : 'Others.'}</title>
  <meta name="description" content={data ? (data.site.metaDescription || data.site.description || 'Streetwear rooted in culture, built for everyone else.') : 'Streetwear rooted in culture, built for everyone else.'} />
  {#if data}
    <meta property="og:type" content="website" />
    <meta property="og:title" content={data.site.metaTitle || data.site.name} />
    <meta property="og:description" content={data.site.metaDescription || data.site.description || 'Streetwear rooted in culture, built for everyone else.'} />
    {#if data.site.ogImage || data.site.logo}<meta property="og:image" content={data.site.ogImage || data.site.logo} />{/if}
  {/if}
</svelte:head>

{#if loading || !data}
  <Loader />
{:else}
  <div class="min-h-screen flex flex-col">
    <Navbar siteName={data.site.name} logo={data.site.logo} logoHeight={data.site.navLogoSize} />
    <div class="flex-1">
      {#if heroEnabled}
        {#key hero}<Hero {hero} />{/key}
        <AnnouncementBar text={data.site.announcement} />
        <ProductGrid products={data.products} currency={data.site.currency} />
      {:else}
        <!-- No hero: clear the fixed navbar (its real height, which follows the logo-size setting), then lead with the announcement bar and New Arrivals. -->
        <div class="pt-[var(--nav-h,61px)]">
          <AnnouncementBar text={data.site.announcement} />
        </div>
        <ProductGrid products={data.products} currency={data.site.currency} mode="arrivals" />
      {/if}

      {#if promo && !promoAfterEditorial}
        <CategoryPromo category={promo.category} products={promo.items} currency={data.site.currency} label={data.site.promotedCategory.label || ''} heading={data.site.promotedCategory.heading || ''} message={data.site.promotedCategory.message || ''} cta={data.site.promotedCategory.cta || ''} />
      {/if}

      {#if !editorialEnabled}
        <!-- featured editorial switched off -->
      {:else if editorialType === 'article'}
        <ArticleSection article={featuredArticle} {allArticles} heading={data.site.featuredEditorialHeading} message={data.site.featuredEditorialMessage} cta={data.site.featuredEditorialCta} />
      {:else}
        <LookbookSection lookbook={featuredLookbook} {allLookbooks} />
      {/if}

      {#if promo && promoAfterEditorial}
        <CategoryPromo category={promo.category} products={promo.items} currency={data.site.currency} label={data.site.promotedCategory.label || ''} heading={data.site.promotedCategory.heading || ''} message={data.site.promotedCategory.message || ''} cta={data.site.promotedCategory.cta || ''} />
      {/if}
    </div>

    <Footer {data} />
  </div>
{/if}